/**
 * Public Route Component
 * 
 * Wraps public routes (login, etc.) to redirect authenticated users to dashboard
 * Shows loading state while checking authentication
 * 
 * Usage:
 * ```tsx
 * <PublicRoute>
 *   <LoginPage />
 * </PublicRoute>
 * ```
 */

import { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '@/lib/auth-context'
import { landingPageFor } from '@/lib/landing'

interface PublicRouteProps {
  children: ReactNode
}

export function PublicRoute({ children }: PublicRouteProps) {
  const { user, isLoading } = useAuth()

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent mb-4"></div>
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    )
  }

  // If authenticated, redirect to appropriate landing page
  if (user) {
    return <Navigate to={landingPageFor(user)} replace />
  }

  // Not authenticated, show public page
  return <>{children}</>
}
